import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { loginFormSchema, type LoginFormData } from '@/schemas/account/loginForm';
import { useAuth } from '@/api';
import { loginAsync, clearAccountLinkRequired } from '@/store/slices/authSlice.ts';
import { useAppDispatch, useAppSelector } from '@/hooks';
import { useUI } from '@/hooks/ui/useUI';
import { ROUTE_DEFINITIONS } from '@/routes';


interface UseLoginReturn {
  register: ReturnType<typeof useForm<LoginFormData>>['register'];
  handleSubmit: (e?: React.BaseSyntheticEvent) => Promise<void>;
  errors: ReturnType<typeof useForm<LoginFormData>>['formState']['errors'];
  isSubmitting: boolean;
  authError: string;
}

export const useLogin = (): UseLoginReturn => {
  const [authError, setAuthError] = useState('');
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { t: translate } = useTranslation();
  const { isAuthenticated, user, loading } = useAuth();
  const accountLinkRequired = useAppSelector((state) => state.auth.accountLinkRequired);
  const { openModal, closeModal } = useUI();

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    setError,
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginFormSchema),
    mode: 'onBlur',
  });

  useEffect(() => {
    if (isAuthenticated && user && accountLinkRequired) {
      openModal('ConfirmModal', {
        variant: 'success',
        title: translate('auth:ssoLinking.modal.title'),
        bodyText: translate('auth:ssoLinking.modal.body'),
        confirmText: translate('auth:ssoLinking.modal.confirm'),
        cancelText: translate('auth:ssoLinking.modal.cancel'),
        onConfirm: () => {
          closeModal('ConfirmModal');
          void navigate(ROUTE_DEFINITIONS.VINCULAR_CUENTA_SSO.path);
        },
        onCancel: () => {
          dispatch(clearAccountLinkRequired());
          closeModal('ConfirmModal');
        },
      });
    }
  }, [
    isAuthenticated, user, accountLinkRequired,
    openModal, closeModal, navigate, dispatch, translate,
  ]);

  const onSubmit = async (data: LoginFormData) => {
    setAuthError('');
    const payload = { userName: data.username, passWord: data.password };

    try {
      const result = await dispatch(loginAsync(payload)).unwrap();
      if (result && result.token) {
        // éxito: redirección se maneja afuera
        return;
      }
    } catch (err: any) {
      const msg = typeof err === 'string'
        ? err
        : err?.description || err?.message || 'Error de conexión';

      const trimmed = msg.trim();
      const isCred = /credenciales/i.test(trimmed);

      if (isCred) {
        setError('username', { message: ' ' });
        setError('password', { message: 'Usuario o contraseña incorrectos' });
        return;
      }
      setError('username', { message: ' ' });
      setError('password', { message: ' ' });
      setAuthError(trimmed);
    }
  };

  return {
    register,
    handleSubmit: handleSubmit(onSubmit),
    errors,
    isSubmitting: isSubmitting || loading,
    authError,
  };
};
